import React, { useContext, useEffect, useMemo } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import T from 'utils/useLocalizer';
import TextField from 'components/form/input/TextField';
import ModalWrapper, { ModalFooter } from 'components/ui/modal/ModalWrapper';
import { IOrderCustomStatusFormData, IOrderSettingsFormData } from 'models/ordersModel';
import { CircleFill } from 'react-bootstrap-icons';
import FormLabel from 'components/form/label/FormLabel';
import OrderStatusWATemplateForm from './OrderStatusWATemplateForm';
import { OrderWAPhoneEmptyContext } from './OrderWAPhoneEmptyContext';
import { useGetUtilityAndMarketingTemplateLookups } from 'services/whatsAppTemplate/whatsAppTemplateService';
import { TEMPLATE_STATUS } from 'models/whatsAppTemplateModel';

export interface Props {
  mode: 'add' | 'edit';
  item?: IOrderCustomStatusFormData;
  show: boolean;
  onClose: () => any;
  onSubmit: (data: IOrderCustomStatusFormData) => any;
}

const statusNameRegex = /^[a-zA-Z0-9 _-]+$/;
const statusArabicNameRegex = /^[\u0600-\u06FF0-9 _-]+$/;

type CustomStatusValidationContext = {
  getExistingStatuses: () => IOrderCustomStatusFormData[];
  mode: 'add' | 'edit';
  currentItemId?: number;
};

const AddCustomStatusModal = ({ mode, item, show, onClose, onSubmit }: Props) => {
  const isDisabled = useContext(OrderWAPhoneEmptyContext);
  const { data, isLoading: isLoadingTemplateList } = useGetUtilityAndMarketingTemplateLookups(true, false);
  const availableNotificationTemplate = useMemo(() => data?.filter((c) => c.statusId === TEMPLATE_STATUS.APPROVED).sort((a, b) => a.name.localeCompare(b.name)), [data]);
  const waTemplateList =
    availableNotificationTemplate?.map((item) => ({
      value: item.id,
      label: item.name,
    })) ?? [];
  const { watch: watchCustomList } = useFormContext<IOrderSettingsFormData>();
  const customStatuses = watchCustomList('customStatuses');

  const customStatusSchema: yup.ObjectSchema<IOrderCustomStatusFormData, CustomStatusValidationContext> = yup.object({
    itemId: yup.number().required(),
    statusId: yup.number().required().min(0, 'orders.order.settings.custom.status.id.form.validation.message'),
    name: yup.string(),
    names: yup.string(),
    englishName: yup
      .string()
      .trim()
      .required('orders.order.settings.custom.status.english.name.form.validation.message')
      .matches(statusNameRegex, 'orders.order.settings.custom.status.name.invalid.message')
      .test('unique-english', 'orders.order.settings.custom.status.english.name.duplicate.message', function (value) {
        const existing = customStatuses;
        const currentId = item?.itemId;
        return !existing?.some((s) => {
          if (mode === 'edit' && s.itemId === currentId) return false;
          return s.englishName?.trim().toLowerCase() === value?.trim().toLowerCase();
        });
      }),
    arabicName: yup
      .string()
      .trim()
      .required('orders.order.settings.custom.status.arabic.name.form.validation.message')
      .matches(statusArabicNameRegex, 'orders.order.settings.custom.status.arabic.name.invalid.message')
      .test('unique-arabic', 'orders.order.settings.custom.status.arabic.name.duplicate.message', function (value) {
        const existing = customStatuses;
        const currentId = item?.itemId;
        return !existing?.some((s) => {
          if (mode === 'edit' && s.itemId === currentId) return false;
          return s.arabicName?.trim() === value?.trim();
        });
      }),
    isNotify: yup.boolean().required(),
    waTemplateId: yup.number().when('isNotify', {
      is: true,
      then: (schema) => schema.required('orders.order.settings.custom.status.notify.template.form.validation.message'),
      otherwise: (schema) => schema.notRequired(),
    }),
    templateName: yup.string().nullable(),
    isUsed: yup.boolean().required(),
  });
  const methods = useForm<IOrderCustomStatusFormData>({
    defaultValues: {
      itemId: 0,
      statusId: 0,
      isNotify: false,
      waTemplateId: null,
      isUsed: false,
    },
    resolver: yupResolver(customStatusSchema),
    mode: 'onChange',
  });
  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    register,
    formState: { isValid, errors },
  } = methods;
  const { onChange } = register('isNotify');

  const resources = {
    addCustomStatusTitle: T('orders.order.settings.custom.status.add.modal.title'),
    editCustomStatusTitle: T('orders.order.settings.custom.status.edit.modal.title'),
    modalDesc: T('orders.order.settings.custom.status.modal.description'),
    previewLabel: T('orders.order.settings.custom.status.modal.preview.label'),
    previewToggle: T('orders.order.settings.custom.status.modal.preview.toggle.label'),
    nameLabel: T('orders.order.settings.custom.status.modal.name.label'),
    nameLabelDesc: T('orders.order.settings.custom.status.modal.english.name.label.description'),
    arabicNameLabel: T('orders.order.settings.custom.status.modal.arabic.name.label'),
    arabicNameLabelDesc: T('orders.order.settings.custom.status.modal.arabic.name.label.description'),
    notifyCustomerLabel: T('orders.order.settings.custom.status.modal.notify.customer.label'),
    notifyCustomerLabelDesc: T('orders.order.settings.custom.status.modal.notify.customer.label.description'),
    closeButton: T('orders.order.settings.custom.status.modal.close.button'),
    saveChangesButton: T('orders.order.settings.custom.status.modal.save.button'),
  };
  const previewName = watch(`englishName`);
  const notifyWatch = watch(`isNotify`);
  const waTemplateWatch = watch(`waTemplateId`);

  useEffect(() => {
    if (mode === 'edit' && item) {
      reset(item);
    }
  }, [item, mode, reset]);

  const handleNotifyToggleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e);
    if (!e.target.checked) {
      setValue(`waTemplateId`, null);
      setValue(`templateName`, null);
      return;
    }

    if (waTemplateWatch && waTemplateWatch > 0) {
      setValue(`waTemplateId`, waTemplateWatch);
      setValue(`templateName`, waTemplateList.find((t) => t.value === waTemplateWatch)?.label ?? null);
      return;
    }

    if (!waTemplateWatch && availableNotificationTemplate && availableNotificationTemplate.length > 0) {
      setValue(`waTemplateId`, Number(availableNotificationTemplate[0].id));
      setValue(`templateName`, availableNotificationTemplate[0].name ?? null);
    }
  };

  const handleTemplateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedTemplateId = Number(e);
    setValue(`templateName`, waTemplateList.find((t) => t.value === selectedTemplateId)?.label ?? null);
  };

  return (
    <ModalWrapper
      show={show}
      onHide={onClose}
      centered
      backdrop="static"
      closeButton
      headerClassName="border-0"
      className="text-start"
      title={mode === 'add' ? resources.addCustomStatusTitle : resources.editCustomStatusTitle}
      subTitle={resources.modalDesc}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}>
        <div className="d-grid gap-0 row-gap-4 px-2 pb-4">
          {/* preview status */}
          <div>
            <label className="small text-muted text-uppercase fw-bold mb-2">{resources.previewLabel}</label>
            <div>
              <span dir="ltr" className="badge bg-light-blue p-2 rounded-pill text-xs truncate-width-200">
                <CircleFill width={7} height={6} /> {previewName === '' ? resources.previewToggle : previewName}
              </span>
            </div>
          </div>

          {/* status english name */}
          <div className="form-group f-s-14 w-100">
            <label className="mb-2 d-flex gap-1">
              <span className="fw-bold">{resources.nameLabel}</span>
              <span className="label-desc-color">{resources.nameLabelDesc}</span>
            </label>
            <TextField
              inputProps={{ ...register(`englishName`) }}
              isError={Boolean(errors.englishName)}
              errorMessage={errors.englishName?.message}
              size="sm"
              maxLength={128}
            />
          </div>

          {/* status arabic name */}
          <div className="form-group f-s-14 w-100">
            <label className="mb-2 d-flex gap-1">
              <span className="fw-bold">{resources.arabicNameLabel}</span>
              <span className="label-desc-color">{resources.arabicNameLabelDesc}</span>
            </label>
            <TextField
              customClassName="text-start"
              inputProps={{ ...register(`arabicName`) }}
              isError={Boolean(errors.arabicName)}
              errorMessage={errors.arabicName?.message}
              size="sm"
              maxLength={128}
            />
          </div>

          {/* notify customer */}
          <div className="form-group border rounded-12 p-3">
            <div className="d-flex flex-md-row flex-column gap-3 justify-content-md-between align-items-center">
              <FormLabel label={resources.notifyCustomerLabel} labelDesc={resources.notifyCustomerLabelDesc} />
              <div className="form-check form-switch d-flex align-items-center gap-2 mb-3">
                <input
                  {...register(`isNotify`)}
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="switchCheckChecked"
                  style={{ width: '50px', height: '25px' }}
                  disabled={isDisabled}
                  onChange={(e) => handleNotifyToggleChange(e)}
                />
              </div>
            </div>
            {notifyWatch && (
              <OrderStatusWATemplateForm
                inputProps={register(`waTemplateId`)}
                toolTipId={`custom-status-notification-template-hint`}
                waTemplateList={waTemplateList ?? []}
                isLoadingTemplateList={isLoadingTemplateList}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleTemplateChange(e)}
              />
            )}
          </div>
        </div>
      </form>
      <ModalFooter className="border-0 justify-content-end px-2 pb-4">
        <button type="button" className="btn btn-secondary ms-3" onClick={onClose}>
          {resources.closeButton}
        </button>
        <button className="btn btn-primary" type="button" onClick={handleSubmit(onSubmit)} disabled={!isValid}>
          {resources.saveChangesButton}
        </button>
      </ModalFooter>
    </ModalWrapper>
  );
};

export default AddCustomStatusModal;
